import { computed } from 'vue';

import { useMutation, useQuery, useQueryCache } from '@pinia/colada';
import { z } from 'zod';

import useCache from '@/lib/local-storage/useCache';
import { STATIC_QUERY_OPTIONS } from '@/shared/pinia/colada';

import { disableFacebookBeta } from './api/disableFacebookBeta';
import { enableFacebookBeta } from './api/enableFacebookBeta';
import { getFacebookBetaStatus } from './api/getFacebookBetaStatus';

export const FACEBOOK_BETA_STATUS_QUERY_KEY = 'facebook-beta-status';

const facebookBetaStatusCache = useCache(FACEBOOK_BETA_STATUS_QUERY_KEY, 'v1.0.0', z.boolean());

export function useFacebookBetaStatus() {
  const { isLoading, state } = useQuery({
    initialData: () => facebookBetaStatusCache.getValue() ?? false,
    key: () => [FACEBOOK_BETA_STATUS_QUERY_KEY],
    query: async () => {
      const result = await getFacebookBetaStatus();

      if (!result.isOk) {
        throw new Error(result.errorMessage);
      }

      facebookBetaStatusCache.setValue(result.enabled);

      return result.enabled;
    },
    ...STATIC_QUERY_OPTIONS,
  });

  const isEnabled = computed(() => state.value.data ?? false);

  return {
    isEnabled,
    isLoading,
  };
}

export function useEnableFacebookBeta() {
  const queryCache = useQueryCache();

  const { asyncStatus, mutateAsync } = useMutation({
    mutation: async (key: string): Promise<{ errorMessage: string; isOk: false } | { isOk: true }> =>
      enableFacebookBeta(key),
    onSuccess: (result) => {
      if (result.isOk) {
        void queryCache.invalidateQueries({ key: [FACEBOOK_BETA_STATUS_QUERY_KEY] });
      }
    },
  });

  return {
    enable: mutateAsync,
    isEnabling: computed(() => asyncStatus.value === 'loading'),
  };
}

export function useDisableFacebookBeta() {
  const queryCache = useQueryCache();

  const { asyncStatus, mutateAsync } = useMutation({
    mutation: async (): Promise<{ errorMessage: string; isOk: false } | { isOk: true }> => disableFacebookBeta(),
    onSuccess: (result) => {
      if (result.isOk) {
        void queryCache.invalidateQueries({ key: [FACEBOOK_BETA_STATUS_QUERY_KEY] });
      }
    },
  });

  return {
    disable: mutateAsync,
    isDisabling: computed(() => asyncStatus.value === 'loading'),
  };
}
